import { Star, TriangleAlert } from "lucide-react";
import { d, moneyDisplay, qtyDisplay } from "@icraftx/shared/v1";
import { cn } from "@/lib/utils";
import type { PosCatalogItem } from "@/shared/api/pos";

export function PosProductTile(props: {
  product: PosCatalogItem;
  cartQty: number;
  favorited: boolean;
  disabled: boolean;
  onAdd: () => void;
  onToggleFavorite: () => void;
  /** Tighter tile for phone rail. */
  compact?: boolean;
}) {
  const {
    product: p,
    cartQty,
    favorited,
    disabled,
    onAdd,
    onToggleFavorite,
    compact = false,
  } = props;
  const stockShort =
    p.kind !== "service" &&
    (d(p.onHand).lt(0) || d(cartQty).gt(d(p.onHand)) || d(p.onHand).eq(0));

  return (
    <div
      className={cn(
        "relative flex flex-col rounded-lg border bg-[var(--color-surface,transparent)]",
        compact ? "min-h-[4.75rem]" : "min-h-24",
        stockShort
          ? "border-[var(--color-destructive)]/55"
          : "border-[var(--color-border)]",
      )}
    >
      <button
        type="button"
        className="absolute right-1 top-1 z-10 rounded-md p-1.5 text-[var(--color-muted)] transition hover:bg-[var(--color-background)] hover:text-[var(--color-foreground)]"
        aria-label={favorited ? "Remove favorite" : "Add favorite"}
        aria-pressed={favorited}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleFavorite();
        }}
      >
        <Star
          className={cn(
            "size-3.5",
            favorited && "fill-[var(--color-foreground)] text-[var(--color-foreground)]",
          )}
        />
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={onAdd}
        className={cn(
          "flex flex-1 flex-col items-start justify-between px-3 py-2 pr-8 text-left transition hover:border-[var(--color-foreground)]/30 disabled:opacity-40",
          compact ? "min-h-[4.75rem]" : "min-h-24",
        )}
      >
        <span className="flex w-full items-start justify-between gap-1">
          <span className="text-sm font-medium leading-snug">{p.name}</span>
          {stockShort ? (
            <TriangleAlert
              className="mt-0.5 size-3.5 shrink-0 text-[var(--color-destructive)]"
              aria-label="Below on-hand"
            />
          ) : null}
        </span>
        {(p.sku || p.barcode) && (
          <span className="mt-0.5 font-mono text-[10px] text-[var(--color-muted)]">
            {p.barcode ?? p.sku}
          </span>
        )}
        <span className="mt-2 flex w-full items-end justify-between gap-1">
          {cartQty > 0 ? (
            <span className="rounded-md bg-[color-mix(in_oklab,var(--color-foreground)_10%,transparent)] px-1.5 py-0.5 font-mono text-[10px] font-semibold tabular-nums">
              ×{cartQty}
            </span>
          ) : (
            <span />
          )}
          <span className="font-mono text-sm tabular-nums">
            ₱{moneyDisplay(p.price)}
          </span>
        </span>
        <span
          className={cn(
            "text-[10px]",
            stockShort
              ? "font-medium text-[var(--color-destructive)]"
              : "text-[var(--color-muted)]",
          )}
        >
          on hand {qtyDisplay(p.onHand)}
        </span>
      </button>
    </div>
  );
}
